import { useCallback, useState, useRef } from "react";
import { useEvent } from "react-use";
import { useUnmount } from "react-use";
import { useSttQuery } from "../queries/sttQuery";
import { dispatchEvent, EVENT } from "tools/events";
import { RecordingResultDetail } from "components/speech/AudioInput";
import { isMicrophoneSupported } from "tools/recorder";
import { RecordingOptions } from "components/speech/AudioInput";
import { convertAudioBlobToMp3Blob, convertBlobToBase64 } from "tools/audioEncoder";

type SpeechOptions = {
  languages?: string[];
  listeningOptions?: RecordingOptions;
  transcription?: boolean;
  onTranscribed?: (text: string) => void;
  onSpeechData?: (audioBase64: string) => void;
  forceMp3?: boolean;
};

const useSpeech = (options?: SpeechOptions) => {
  const { transcription = true, onTranscribed, onSpeechData } = options || {};
  const { isTranscribing: transcribing, transcribe } = useSttQuery();
  const [listening, setIsListening] = useState(false);
  const canceled = useRef(false);

  const handleRecordingDone = useCallback(
    async (e: CustomEvent<RecordingResultDetail>) => {
      if (!listening || canceled.current) return;
      const { audioBlob } = e.detail;
      const resultBlob = options?.forceMp3 ? await convertAudioBlobToMp3Blob(audioBlob) : audioBlob;
      const base64Audio = await convertBlobToBase64(resultBlob);
      if (base64Audio) {
        onSpeechData?.(base64Audio);
        if (!transcription) return;
        transcribe({
          audio: base64Audio,
          audioFormat: resultBlob.type,
          onTranscribed: (text) => {
            onTranscribed?.(text);
          },
          languages: options?.languages
        });
      }
    },
    [transcribe, options, listening, canceled, onTranscribed, transcription, onSpeechData]
  );

  useEvent(EVENT.recordingResult, handleRecordingDone);
  useEvent(EVENT.recordingStopped, () => {
    setIsListening(false);
  });

  const startListening = useCallback(() => {
    dispatchEvent(EVENT.startRecording, {
      ...options?.listeningOptions
    });
    canceled.current = false;
    setIsListening(true);
  }, [options?.listeningOptions]);

  const stopListening = useCallback(() => {
    dispatchEvent(EVENT.stopRecording);
  }, []);

  const cancelListening = useCallback(() => {
    canceled.current = true;
    dispatchEvent(EVENT.stopRecording);
  }, [canceled]);

  useUnmount(() => {
    if (listening) {
      cancelListening();
    }
  });

  return {
    transcribing,
    listening,
    startListening,
    stopListening,
    cancelListening,
    supported: isMicrophoneSupported()
  };
};

export default useSpeech;
